import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@Utils';

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        brand: 'bg-brand-50 border-brand-600 text-brand-700',
        green: 'bg-success-50 border-success-200 text-success-700',
        yellow: 'bg-warning-50 border-warning-200 text-warning-700',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const dotVariants: Partial<Record<NonNullable<VariantProps<typeof badgeVariants>['variant']>, string>> = {
  brand: 'bg-brand-600',
  green: 'bg-success-400',
  yellow: 'bg-warning-500',
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  /** Render a colored dot before the children — only applies to brand/green/yellow variants. */
  dot?: boolean;
}

const Badge = ({ className, variant, dot, children, ...props }: BadgeProps) => {
  const dotClass = variant ? dotVariants[variant] : undefined;
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {dot && dotClass && <span className={cn('h-1.5 w-1.5 rounded-full', dotClass)} />}
      {children}
    </span>
  );
};

export { Badge, badgeVariants };
